import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import Typography from '../../../basic/Typography';
import { useAuth } from '../context/AuthContext';
import { getWalletBalance } from '../services/authApi';
import { formatCreditsAsUsd } from '../utils/credits';
import { AuthLayout } from './AuthLayout';
import styles from './AccountPage.module.css';

type ReturnStatus = 'polling' | 'success' | 'error';

const MAX_POLL_ATTEMPTS = 15;
const POLL_INTERVAL_MS = 2000;

export function ReturnPage() {
  const { status, me, refreshMe } = useAuth();
  const navigate = useNavigate();

  const [returnStatus, setReturnStatus] = useState<ReturnStatus>('polling');
  const [newBalance, setNewBalance] = useState<number | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const initialBalance = me?.balance ?? null;

  const pollBalance = useCallback(async () => {
    let attempts = 0;
    const poll = async (): Promise<void> => {
      attempts += 1;
      try {
        const resp = await getWalletBalance();
        if (initialBalance !== null && resp.balance > initialBalance) {
          setNewBalance(resp.balance);
          setReturnStatus('success');
          await refreshMe();
          return;
        }
        if (attempts >= MAX_POLL_ATTEMPTS) {
          setNewBalance(resp.balance);
          setReturnStatus('success');
          await refreshMe();
          return;
        }
        setTimeout(() => void poll(), POLL_INTERVAL_MS);
      } catch (err) {
        setErrorMsg(err instanceof Error ? err.message : 'Failed to refresh balance');
        setReturnStatus('error');
      }
    };
    await poll();
  }, [initialBalance, refreshMe]);

  useEffect(() => {
    if (status !== 'authenticated') return;
    void pollBalance();
  }, [status, pollBalance]);

  useEffect(() => {
    if (returnStatus !== 'success') return;
    const timer = setTimeout(() => {
      navigate('/auth/account', { replace: true });
    }, 3000);
    return () => clearTimeout(timer);
  }, [returnStatus, navigate]);

  if (status === 'anonymous') {
    return <Navigate to="/auth/login" replace />;
  }

  return (
    <AuthLayout title="Payment Complete">
      <div className={styles.section}>
        {returnStatus === 'polling' && (
          <>
            <div className={styles.loadingRow}>
              <div className="loading-spinner" />
              <Typography variant="body1">Refreshing your balance...</Typography>
            </div>
            <Typography variant="body2" color="secondary">
              This may take a few moments.
            </Typography>
          </>
        )}

        {returnStatus === 'success' && (
          <>
            <div className={styles.successBox}>
              <Typography variant="h4" gutterBottom>
                Balance Updated
              </Typography>
              <Typography variant="h3" color="primary">
                {formatCreditsAsUsd(newBalance)}
              </Typography>
            </div>
            <Typography variant="body2" color="secondary">
              Redirecting to your account...
            </Typography>
          </>
        )}

        {returnStatus === 'error' && (
          <>
            <div className={styles.errorBox}>
              <Typography variant="body1" style={{ color: '#d32f2f' }}>
                {errorMsg ?? 'Something went wrong'}
              </Typography>
            </div>
            <Typography variant="body2" color="secondary">
              Your payment may still be processing. Check your account in a few minutes.
            </Typography>
          </>
        )}
      </div>
    </AuthLayout>
  );
}
